'use client';

import { useMutation } from '@apollo/client/react';
import { useSideNav } from '@components/SideNav2/context';
import { Center, Text, Tooltip } from '@mantine/core';
import { SetFeatureDiscoveryStateDocument } from '@modules/global-modals/modals/feature-discovery/feature-discovery.graphql';
import { useShowNewFeatureIndicator } from '@modules/global-modals/modals/feature-discovery/useShowNewFeatureIndicator';
import { useNavigationInterceptor } from '@modules/navigation-guard/use-navigation-guard';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import type { ReactNode } from 'react';

import {
  navLinkListItemStyle,
  navLinkStyle,
  newFeatureIndicatorStyle,
  textStyle,
} from './styles.css';

interface NavLinkProps {
  prefetch?: boolean;
  label: string;
  href: string;
  leadingIcon: ReactNode;
  onClick?: () => void;
  /**
   * Optional feature discovery key.
   * When provided, NavLink shows the default red dot and marks the key as ACTED on click.
   */
  featureDiscoveryKey?: string;
  /** Optional trailing element shown when sidebar is expanded; overrides default discovery indicator */
  trailingIndicator?: ReactNode;
  /**
   * Extra active condition beyond the href prefix, for routes that share no
   * path prefix with this link (e.g. the identity editor). Receives the current
   * pathname + search params.
   */
  isActiveOverride?: (ctx: {
    pathname: string;
    searchParams: { get(name: string): string | null };
  }) => boolean;
}

export function NavLink({
  prefetch,
  leadingIcon,
  label,
  href,
  onClick,
  featureDiscoveryKey,
  trailingIndicator,
  isActiveOverride,
}: NavLinkProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const { isExpanded } = useSideNav();
  const showNewFeatureIndicator = useShowNewFeatureIndicator(featureDiscoveryKey);
  const [setFeatureState] = useMutation(SetFeatureDiscoveryStateDocument);
  const interceptNavigation = useNavigationInterceptor();

  const active = Boolean(
    pathname &&
      (pathname.startsWith(href) ||
        isActiveOverride?.({ pathname, searchParams: searchParams ?? new URLSearchParams() })),
  );

  function handleMouseEnter() {
    if (prefetch === false) {
      return;
    }

    router.prefetch(href);
  }

  function handleClick() {
    if (featureDiscoveryKey && showNewFeatureIndicator) {
      setFeatureState({
        variables: {
          input: {
            keys: [featureDiscoveryKey],
            status: 'ACTED',
          },
        },
      });
    }

    onClick?.();
  }

  const renderedTrailingIndicator =
    trailingIndicator ??
    (showNewFeatureIndicator ? <div className={newFeatureIndicatorStyle} /> : undefined);

  return (
    <li className={navLinkListItemStyle}>
      <Tooltip label={label} position="right" disabled={isExpanded}>
        <Link
          className={navLinkStyle({ active, collapsed: !isExpanded })}
          href={href}
          onMouseEnter={handleMouseEnter}
          onClick={handleClick}
          onNavigate={(event) => {
            // A page with unsaved changes (e.g. the identity editor) may block
            // the navigation and take over with its own confirm dialog.
            if (interceptNavigation(href)) {
              event.preventDefault();
            }
          }}
          prefetch={prefetch} // We handle prefetching manually to avoid prefetching all links on page load
        >
          <Center>{leadingIcon}</Center>
          <Text className={textStyle({ collapsed: !isExpanded })} size="xs" fw={600}>
            {label}
          </Text>
          {renderedTrailingIndicator && isExpanded && renderedTrailingIndicator}
        </Link>
      </Tooltip>
    </li>
  );
}
